<template>
  <v-container fluid>
    <!-- header: same topbar as the Enedis view, with the view switch on the right -->
    <div class="topbar">
      <div class="topbar__row">
        <div class="topbar__badge"><v-icon size="22">mdi-wrench-clock</v-icon></div>
        <div class="topbar__title">
          <div class="topbar__name">{{ $filters.capitalize($t(pageTitle, 1)) }}</div>
          <div class="topbar__sub">
            <span>{{ $filters.capitalize($t(viewTitle)) }}</span>
            <template v-if="view !== 'doc' && selectedFilters.length">
              <span class="topbar__dot"></span>
              <span><v-icon size="x-small" class="mr-1">mdi-filter-variant</v-icon>{{ selectedFilters.join(' · ') }}</span>
            </template>
          </div>
        </div>
        <div class="topbar__actions">
          <v-btn-toggle
              v-model="view"
              color="primary"
              variant="outlined"
              density="comfortable"
              divided
              mandatory
              >
              <v-btn v-for="button in buttons" :key="button.viewName" :value="button.viewName" :prepend-icon="button.icon">
                <span class="d-none d-sm-inline">{{ $filters.capitalize($t(button.title)) }}</span>
              </v-btn>
          </v-btn-toggle>
        </div>
      </div>
    </div>
    <!-- filters: same inline bar as the Enedis view -->
    <v-sheet rounded="lg" class="filters" elevation="0" v-show='view != "doc"'>
      <v-text-field
          v-show='view != "regulatory"'
          v-model="searchFilter"
          :label="$filters.capitalize($t('search'))"
          prepend-inner-icon="mdi-magnify"
          variant="solo-filled"
          flat
          density="compact"
          hide-details
          clearable
          class="filters__search"
          ></v-text-field>
      <v-select
          v-model="regionFilter"
          :items="regions"
          :label="$filters.capitalize($t('stockages.region'))"
          :disabled="!isNone(powerStationFilter)"
          prepend-inner-icon="mdi-map-marker-outline"
          variant="solo-filled"
          flat
          density="compact"
          hide-details
          clearable
          class="filters__select"
          ></v-select>
      <v-autocomplete
          v-show='view != "regulatory"'
          v-model="powerStationFilter"
          :items="power_stations"
          item-title="name"
          item-value="pk"
          :label="$filters.capitalize($t('power_stations.label'))"
          prepend-inner-icon="mdi-transmission-tower"
          variant="solo-filled"
          flat
          density="compact"
          hide-details
          clearable
          class="filters__select"
          ></v-autocomplete>
      <v-select
          v-show='view != "regulatory"'
          v-model="producerTypeFilter"
          :items="producerTypes"
          :label="$filters.capitalize($t('producer_types.label'))"
          prepend-inner-icon="mdi-wind-turbine"
          variant="solo-filled"
          flat
          density="compact"
          hide-details
          clearable
          class="filters__select"
          ></v-select>
    </v-sheet>
    <!-- views  -->
    <component
        :is="currentComponent"
        v-if="currentComponent"
        v-bind="view === 'doc' ? {} : { regionFilter, powerStationFilter, producerTypeFilter, searchFilter }"
        />
  </v-container>
</template>

<script>
  import { defineComponent } from 'vue';
  import { markRaw } from "vue";
  import _ from 'lodash';
  import { isNone } from "@/functions";
  import moment from "moment";
  import ValidationList from "@/components/maintenance/ValidationList";
  import MaintenanceView from "@/components/maintenance/MaintenanceRegulatory";
  import SummaryView from "@/components/maintenance/MaintenanceSummary";
  import DocumentationView from "@/components/maintenance/DocumentationView";

  export default defineComponent({
    components: {
      MaintenanceView: MaintenanceView,
      ValidationList: ValidationList,
      SummaryView: SummaryView,
      DocumentationView: DocumentationView,
    },
    mounted(){
      this.$store.dispatch('power_stations/getItems')
      this.$store.dispatch('verif_machines/getProducerTypes').then((items)=>{
        this.producerTypes = items.map(item => ({
        value: item.pk,
        title: item.name,
        }));
      })
    },

    computed: {
      power_station_objects: function(){
        return this.$store.state.power_stations.items
      },

      power_stations: function() {
        let ret_ = this.power_station_objects
        if (this.regionFilter) ret_ = ret_.filter(
          (i)=>i.stockage.region==this.regionFilter)
        return ret_
      },
      regions: function () {
        return [...new Set(this.power_station_objects.map(item => item.stockage.region))]
      },
      viewTitle() {
        const current = this.buttons.find(button => button.viewName === this.view)
        return current ? current.title : ''
      },
      // what the filter bar currently narrows to, for the header subtitle
      selectedFilters() {
        const ret = []
        if (this.regionFilter) ret.push(this.regionFilter)
        if (this.powerStationFilter) {
          const ps = this.power_stations.find(p => p.pk == this.powerStationFilter)
          if (ps) ret.push(ps.name)
        }
        if (this.producerTypeFilter) {
          const pt = this.producerTypes.find(p => p.value == this.producerTypeFilter)
          if (pt) ret.push(pt.title)
        }
        if (this.searchFilter) ret.push(`"${this.searchFilter}"`)
        return ret
      },
      currentComponent() {
        const currentButton = this.buttons.find(button => button.viewName === this.view);
        return currentButton ? currentButton.component : null;
      },
    },
    methods: {
      isNone,
    },

    data() {
      return {
        pageTitle: "maintenances.label",
        searchFilter: '',
        regionFilter: undefined,
        powerStationFilter: undefined,
        producerTypeFilter: undefined,
        producerTypes: [],
        view: 'summary',
        buttons: [
          { viewName: "summary", title: "maintenances.summary", icon: "mdi-view-dashboard-outline", component: markRaw(SummaryView) },
          { viewName: "regulatory", title: "maintenances.type.regulatory", icon: "mdi-file-certificate-outline", component: markRaw(MaintenanceView) },
          //  { viewName: "validation", title: "maintenances.validation", component: markRaw(ValidationList) }, // to be implemented later
          { viewName: "doc", title: "menu.documentation", icon: "mdi-book-open-variant", component: markRaw(DocumentationView) },
        ],
      };
    },
  });
</script>
<style scoped>
.topbar {
  margin-bottom: 12px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}
.topbar::before {
  content: "";
  display: block;
  height: 3px;
  background: linear-gradient(90deg, #1976d2 0%, #42a5f5 55%, #43a047 100%);
}
.topbar__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  flex-wrap: wrap;
}
.topbar__badge {
  width: 40px;
  height: 40px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #e3f2fd;
  color: #1976d2;
}
.topbar__title {
  flex: 1 1 200px;
  min-width: 0;
}
.topbar__name {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
}
.topbar__sub {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  overflow: hidden;
}
.topbar__dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
}
.topbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
}
.filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 12px;
  margin: 12px 0;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.filters__search {
  flex: 1 1 220px;
  max-width: 320px;
}
.filters__select {
  flex: 1 1 200px;
  max-width: 300px;
}
</style>
